import { useCallback } from 'react'
import { Mic2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
  type CharacterVoiceCatalogueEntry,
  type VoiceCatalogueSelectionTargetType,
} from '../../api/client'
import { createNadDebug } from '../../utils/debugLog'
import { useVoiceCatalogueModal } from '../canvas-v2/nodes/shared/VoiceCatalogueModalProvider'

const voiceCatalogueApplyLog = createNadDebug('dw:voice-catalogue-apply')

export interface VoiceCatalogueSelectionContext {
  type: VoiceCatalogueSelectionTargetType
  node_id: string
  target_id?: string | null
  voice_role_id?: string | null
  target_label?: string | null
  selected_voice_ref?: string | null
}

export interface VoiceCatalogueAppliedAnswer {
  selected_option: string
  tool_result_content: string
}

export interface VoiceCatalogueActionCardProps {
  question: string
  selectionContext: VoiceCatalogueSelectionContext
  disabled?: boolean
  onApplied: (answer: VoiceCatalogueAppliedAnswer) => void
}

export function VoiceCatalogueActionCard({
  question,
  selectionContext,
  disabled = false,
  onApplied,
}: VoiceCatalogueActionCardProps) {
  const { t } = useTranslation('chat')
  const { openVoiceCatalogueModal } = useVoiceCatalogueModal()

  const handleApply = useCallback(
    (voice: CharacterVoiceCatalogueEntry) => {
      const applyArgs: {
        target_type: VoiceCatalogueSelectionTargetType
        node_id: string
        target_id?: string
        voice_role_id?: string
        voice_ref: string
      } = {
        target_type: selectionContext.type,
        node_id: selectionContext.node_id,
        voice_ref: voice.voice_ref,
      }
      if (selectionContext.target_id) {
        applyArgs.target_id = selectionContext.target_id
      }
      if (selectionContext.voice_role_id) {
        applyArgs.voice_role_id = selectionContext.voice_role_id
      }
      voiceCatalogueApplyLog({
        event: 'voice-catalogue-apply:selected-for-director',
        targetType: selectionContext.type,
        nodeId: selectionContext.node_id,
        targetId: selectionContext.target_id,
        voiceRoleId: selectionContext.voice_role_id,
        voiceRef: voice.voice_ref,
        voiceName: voice.name,
      })
      onApplied({
        selected_option: voice.name,
        tool_result_content: [
          'Voice catalogue selection completed.',
          'Call apply_voice_catalogue_selection with these arguments:',
          JSON.stringify(applyArgs),
        ].join(' '),
      })
    },
    [
      onApplied,
      selectionContext.node_id,
      selectionContext.target_id,
      selectionContext.type,
      selectionContext.voice_role_id,
    ],
  )

  const openCatalogue = useCallback(() => {
    openVoiceCatalogueModal({
      kind: selectionContext.type === 'video_editor_change_voice'
        ? 'video_editor_change_voice'
        : 'sound_expert',
      targetLabel: selectionContext.target_label || question,
      selectedVoiceRef: selectionContext.selected_voice_ref,
      onApply: handleApply,
    })
  }, [handleApply, openVoiceCatalogueModal, question, selectionContext])

  return (
    <div className="rounded-xl border border-border-subtle bg-surface-0 p-3 shadow-sm">
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-input-border bg-input-bg text-primary">
          <Mic2 className="h-4 w-4" strokeWidth={2} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-foreground">
            {t('voiceCatalogueActionCard.title')}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">{question}</p>
          {selectionContext.target_label ? (
            <p className="mt-1 truncate text-xs text-text-muted">
              {selectionContext.target_label}
            </p>
          ) : null}
        </div>
      </div>
      <button
        type="button"
        className="mt-3 flex h-9 w-full items-center justify-center gap-2 rounded-lg border border-input-border bg-surface-1 px-3 text-sm font-medium text-foreground hover:border-input-border-hover hover:bg-surface-3 disabled:cursor-not-allowed disabled:opacity-60"
        onClick={openCatalogue}
        disabled={disabled}
      >
        {t('voiceCatalogueActionCard.openCatalogue')}
      </button>
    </div>
  )
}
